<script lang="ts">
  import {crossfade, fade} from 'svelte/transition';
  import InlineSVG from 'svelte-inline-svg';

  import type {Media} from '../../types/project';

  import {youtubeVideoUrlRegex, driveVideoUrlRegex} from '../../constants/urls';

  import previousIcon from '../../assets/icons/chevron-thin-left.svg';
  import nextIcon from '../../assets/icons/chevron-thin-right.svg';

  export let media: Array<Media> = [];
  let selectedMediaIndex = 0;
  let videoEmbedUrl: string | null;

  // Convert video URL to iframe src format
  function getIframeUrl(url: string) {
    let match;
    if ((match = url.match(youtubeVideoUrlRegex))) {
      const videoId = match?.groups?.videoId;
      return `https://www.youtube.com/embed/${videoId}`;
    } else if ((match = url.match(driveVideoUrlRegex))) {
      const videoId = match?.groups?.videoId;
      return `https://drive.google.com/file/d/${videoId}/preview`;
    }
    return null;
  }

  $: {
    const currentMedia = media[selectedMediaIndex];
    if (currentMedia.type === 'video') {
      videoEmbedUrl = getIframeUrl(currentMedia.url);
    }
  }

  // Make sure videos will always come after images in the slider.
  $: sortedMedia = media.sort((a, b) => {
    if (a.type === b.type) {
      return 0;
    }
    return a.type > b.type ? 1 : -1;
  });

  $: selectedMedia = sortedMedia.length
    ? sortedMedia[selectedMediaIndex]
    : null;

  // Reset image index when props changed
  $: {
    if (media.length) {
      selectedMediaIndex = 0;
    }
  }

  const [send, receive] = crossfade({
    duration: 100,
    // @ts-ignore: Type 'CrossfadeParams' is not assignable to type 'FadeParams'.
    fallback: fade
  });

  // Proper modulo function, which also works for negative numbers. By default the JS modulo operator is a "remainder" function which will return negative values.
  const mod = (n: number, m: number) => {
    return ((n % m) + m) % m;
  };
</script>

<style lang="postcss">
  .container {
    position: relative;
    width: 100%;
    padding-bottom: calc(100% / 16 * 9); /* aspect ratio 16:9 */
    overflow: hidden;
    border-bottom: 1px solid var(--ubilabs-gray-light);
  }

  .container > div {
    position: absolute;
    width: 100%;
    height: 100%;
    background-color: var(--ubilabs-gray-light);
  }

  .media {
    position: absolute;
    width: 100%;
    height: 100%;
    overflow: hidden;
  }

  .media img {
    width: 100%;
    height: 100%;
  }

  .media > img {
    position: absolute;
    top: 0;
    object-fit: contain;
  }

  .blurredBackgroundImage {
    width: 100%;
    height: 100%;
    filter: blur(20px);
    transform: scale(1.1);
  }

  .media > iframe {
    width: 100%;
    height: 100%;
  }

  .navigationButton {
    position: absolute;
    top: calc((100% - max(4rem, 14vw)) / 2);
    display: flex;
    align-items: center;
    justify-content: center;
    width: max(4rem, 14vw);
    height: max(4rem, 14vw);
    padding: 0;
    color: var(--ubilabs-white);
    font-size: 0.75rem;
    background: radial-gradient(
      circle,
      rgba(var(--ubilabs-black-rgb), 0.15) 0,
      rgba(var(--ubilabs-black-rgb), 0) 65%
    );
  }

  @media (min-width: 640px) {
    .navigationButton {
      font-size: 1rem;
    }
  }

  @media (min-width: 768px) {
    .navigationButton {
      font-size: 1.5rem;
    }
  }

  .navigationButton:hover,
  .navigationButton:focus-visible {
    background: radial-gradient(
      circle,
      rgba(var(--ubilabs-black-rgb), 0.3) 0,
      rgba(var(--ubilabs-black-rgb), 0) 65%
    );
  }

  .navigationButton :global(svg) {
    stroke: #fff;
  }

  .navigationButton.left {
    left: calc(max(4rem, 14vw) * -0.5);
    padding-left: calc(max(4rem, 14vw) * 0.3);
    border-radius: 0 50% 50% 0;
  }

  .navigationButton.right {
    right: calc(max(4rem, 14vw) * -0.5);
    padding-right: calc(max(4rem, 14vw) * 0.3);
    border-radius: 50% 0 0 50%;
  }

  .caption {
    position: absolute;
    bottom: 0;
    display: flex;
    align-items: center;
    width: 100%;
    height: 3rem;
    padding: 0.25rem 0.5rem;
    background: rgba(var(--ubilabs-white-rgb), 0.7);
    font-size: 0.625rem;
  }

  @media (min-width: 640px) {
    .caption {
      height: 3.5rem;
      padding: 0.5rem 1rem;
      font-size: 0.75rem;
    }
  }

  @media (min-width: 768px) {
    .caption {
      height: 4rem;
      padding: 0.5rem 1.5rem;
      font-size: var(--ubilabs-default-font-size);
    }
  }

  .caption > span {
    max-height: 100%;
    overflow: auto;
  }

  .dots {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: center;
    height: 100%;
    margin: 0.5rem 0.25rem 1rem;
  }

  @media (min-width: 768px) {
    .dots {
      margin: 0.5rem 0.5rem 1rem;
    }
  }

  .dot {
    width: 2rem;
    height: 1rem;
    padding: 0;
    border: 0.3rem solid var(--ubilabs-white);
    border-radius: 0.5rem;
    background-color: var(--ubilabs-gray);
  }

  .dot:hover,
  .dot:focus-visible {
    background-color: var(--ubilabs-gray-dark-1);
  }

  .dot.selected {
    background-color: var(--ubilabs-blue-light-1);
  }
</style>

{#if media?.length}
  <div class="container">
    <div>
      {#each sortedMedia as mediaObject, mediaIndex}
        {#if mediaIndex === selectedMediaIndex}
          <div class="media">
            {#if mediaObject.type === 'image'}
              <div class="blurredBackgroundImage">
                <img
                  src={mediaObject.url}
                  alt={mediaObject.caption}
                  in:receive|local={{key: mediaIndex}}
                  out:send|local={{key: mediaIndex}} />
              </div>
              <img
                src={mediaObject.url}
                alt={mediaObject.caption}
                in:receive|local={{key: mediaIndex}}
                out:send|local={{key: mediaIndex}} />
            {:else if mediaObject.type === 'video' && videoEmbedUrl}
              <iframe
                title="Embedded Video"
                src={videoEmbedUrl}
                frameborder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen />
            {/if}
          </div>
        {/if}
      {/each}

      {#if media?.length > 1}
        <!-- Back button -->
        <button
          class="navigationButton left"
          on:click={() => {
            selectedMediaIndex = mod(selectedMediaIndex - 1, media.length);
          }}>
          <InlineSVG src={previousIcon} />
        </button>

        <!-- Next button -->
        <button
          class="navigationButton right"
          on:click={() => {
            selectedMediaIndex = mod(selectedMediaIndex + 1, media.length);
          }}>
          <InlineSVG src={nextIcon} />
        </button>
      {/if}

      <!-- Bottom container -->
      {#if selectedMedia && 'caption' in selectedMedia && selectedMedia.caption}
        <div class="caption">
          <span>{selectedMedia.caption}</span>
        </div>
      {/if}
    </div>
  </div>

  <!-- Dot indicator -->
  {#if selectedMedia && media?.length > 1}
    <div class="dots">
      {#each sortedMedia as _, index}
        <button
          class="dot"
          class:selected={index === selectedMediaIndex}
          on:click={() => {
            selectedMediaIndex = index;
          }} />
      {/each}
    </div>
  {/if}
{/if}
